import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import H4 from '@base/H4/H4';
import Paper from '@base/Paper/Paper';
import styled from '@emotion/styled';
import { Spacings, mediaQuery } from '@styles/index';
import { goBack } from '@utils/breadcrumbs';
import Dropdown from '@comp/Dropdown/Dropdown';
import { useRouter } from 'next/router';
import useInfiniteList from '@hooks/useInfiniteList';
import { useEffect, useRef, useState } from 'react';
import DppCarousel from '@skeletons/Dpp/DppCarousel';
import { useSWRConfig } from 'swr';
import { getIsSubscribedToGoal } from '@api/selectors/subscription';
import isEqual from 'lodash/isEqual';
import DppCard from './DppCard';
import { stripLength } from '../constants';
import { getFilterQueryParams, getFiltersApplied } from '../utils';
import EmptyState from './EmptyState';
import usePracticesByFilters from './usePracticesByFilters';

const Wrapper = styled.div`
  display: grid;
  grid-column: span 24;
  grid-template-columns: repeat(4, 272px);
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-row-gap: ${Spacings.SPACING_12B};

  ${mediaQuery.uptoTablet} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${mediaQuery.uptoMobile} {
    grid-template-columns: repeat(auto-fit, minmax(272px, 1fr));
  }
`;

const Header = styled(Paper)`
  margin-top: ${Spacings.SPACING_10B};
  padding: ${Spacings.SPACING_10B} ${Spacings.SPACING_6B};
  display: flex;
  grid-column: span 24;
  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    box-shadow: none;
    background: var(--color-base-0);
    padding: 0;
    margin: ${Spacings.SPACING_6B} 0 0;
    display: flex;
    flex-direction: column;
  }
`;

const Educator = styled(H4)`
  margin-left: ${Spacings.SPACING_3B};
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    margin-left: 0;
    margin-top: ${Spacings.SPACING_6B};
    font-size: 22px;
    line-height: 120%;
  }
`;

const TopWrapper = styled.div`
  grid-column: span 24;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  position: sticky;
  top: ${Spacings.SPACING_18B};
  z-index: 1;
  background: var(--color-base-0);
  margin-bottom: ${({ $isFilters }) => {
    return $isFilters ? `${Spacings.SPACING_8B}` : `${Spacings.SPACING_0B}`;
  }};
  ${mediaQuery.uptoMobile} {
    position: relative;
    grid-column: span 10;
    grid-template-columns: repeat(10, 1fr);
    grid-column-gap: ${Spacings.SPACING_2B};
    top: 0;
  }
`;

const FilterWrapper = styled.div`
  grid-column: span 24;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin: 0 -${Spacings.SPACING_4B};
    grid-column: span 10;
    display: flex;
    overflow: auto;
    overscroll-behavior: unset;
    scroll-behavior: smooth;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    padding: ${Spacings.SPACING_4B} 0 ${Spacings.SPACING_6B}
      ${Spacings.SPACING_4B};
    & > button {
      box-shadow: none;
      white-space: nowrap;
    }
    &.sticky {
      position: fixed;
      top: ${Spacings.SPACING_12B};
      width: 100%;
      background: var(--color-i-white);
      box-shadow: inset 0px -1px 0px var(--color-shadow);
    }
  }
`;

const Loader = () => <DppCarousel number={4} />;

const EducatorPractices = ({
  authorID,
  profileInfo,
  fallbackPractices,
  meData,
  isLoggedIn,
  filtersAppliedOnServer
}) => {
  const router = useRouter();
  const { cache } = useSWRConfig();
  const filterWrapperRef = useRef('');

  const [filtersApplied, setFiltersApplied] = useState(filtersAppliedOnServer);
  const {
    practicesByFilters = {},
    isValidating: isFetchingFilteredPractices,
    setSize: setNumberOfPages
  } = usePracticesByFilters(
    { userName: authorID?.slice(1), type: '' },
    { filtersApplied: getFiltersApplied(router.query) },
    { fallbackData: fallbackPractices }
  );

  const loadMorePractices = async () => {
    if (!isFetchingFilteredPractices && practicesByFilters.next)
      setNumberOfPages((nPages) => nPages + 1);
  };

  const renderDPPCard = (practice = {}) => {
    const isSubscribed = getIsSubscribedToGoal(practice.value?.goalUid)(meData);
    return (
      <DppCard
        practice={practice.value}
        isSubscribed={isSubscribed}
        isLoggedIn={isLoggedIn}
      />
    );
  };

  const FilteredPracticesDataToRender = useInfiniteList({
    loadMore: loadMorePractices,
    data: practicesByFilters,
    renderItem: renderDPPCard,
    Loader
  });

  useEffect(() => {
    const currentFilter = getFiltersApplied(router.query);
    if (!isEqual(currentFilter, filtersApplied)) {
      setFiltersApplied(currentFilter);
      cache.clear();
      setNumberOfPages(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query]);

  const selectFilter = (filter) => (option) => {
    const applyFilters = getFiltersApplied(router.query);
    const queryParams = getFilterQueryParams({
      filter,
      option,
      filtersApplied: applyFilters
    });

    router.push(
      `/[authorID]/practices/?${queryParams}`,
      `/${authorID}/practices/?${queryParams}`,
      { shallow: true }
    );
  };

  useEffect(() => {
    function stickyFilter() {
      if (window.pageYOffset > 106) {
        filterWrapperRef.current.classList.add('sticky');
      } else {
        filterWrapperRef.current.classList.remove('sticky');
      }
    }

    window.addEventListener('scroll', stickyFilter);
    return () => {
      window.removeEventListener('scroll', stickyFilter);
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      cache.clear();
      setNumberOfPages(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <>
      <TopWrapper>
        <Header>
          <BreadCrumbs tags={[]} onBackClick={goBack} />
          <Educator>{`Practice by ${profileInfo?.firstName || ''} ${
            profileInfo?.lastName || ''
          }`}</Educator>
        </Header>
        <FilterWrapper ref={filterWrapperRef}>
          {practicesByFilters?.filters?.map((filter) => {
            const relevantOptions = filtersApplied[filter.key];
            const selectedOption = filter.values.find(
              (value) => relevantOptions.id === value.id
            );
            return (
              <Dropdown
                options={filter.values}
                key={filter.key}
                selectedOption={selectedOption || filter.values[0]}
                onOptionClick={selectFilter(filter.key)}
                stripLength={stripLength[filter.key]}
                showSelectedBorder={false}
                menuWidth="176px"
              />
            );
          })}
        </FilterWrapper>
      </TopWrapper>
      <Wrapper>{FilteredPracticesDataToRender}</Wrapper>

      {isFetchingFilteredPractices && <DppCarousel number={4} />}
      {!isFetchingFilteredPractices &&
        practicesByFilters.results?.length === 0 && <EmptyState />}
    </>
  );
};

export default EducatorPractices;
